import { useMemo, useState } from 'react';
import { fmtNum, fmtTRY } from '../../yeni-design/primitives';
import { useSaSorgu } from '../useSatisVerisi';
import type { SaKategoriSatiri } from '../ozetHesaplari';
import { oranMetni } from '../bicim';
import { digerKategoriler } from '../KategoriBlok';
import type { DetayGovdeProps } from './detayTipleri';
import { AramaKutusu, aramaSuz, DetayBolum, DetayMesaj, DetayYukleniyor } from './ortak';

/**
 * S295 (kullanici 2026-09-23): kategori tablosundaki "Diger N kategori" satirinin icindekiler. Veri sayfanin
 * 990051 anahtarindan (`onbellekten`): yeni istek yok, toplam tablodaki Diger satiriyla ayni. Kolonlar kategori
 * tablosununkiyle ayni; kategori adi o kategorinin detayini acar (modal geri yigini).
 */
export function DigerKategorilerDetay({ kaynaklar, donem, oncekiDonem, sube, kanal, ac }: DetayGovdeProps) {
  const [arama, setArama] = useState('');
  const q = useSaSorgu<SaKategoriSatiri>(
    kaynaklar.kategori, { donem, sube, kanal, onceki: oncekiDonem }, true, { onbellekten: true },
  );
  const veri = q.data?.data;
  const satirlar = useMemo(() => digerKategoriler(veri ?? []), [veri]);
  const gorunen = useMemo(() => aramaSuz(satirlar, arama, (k) => [k.ad]), [satirlar, arama]);

  let govde;
  if (q.isLoading) govde = <DetayYukleniyor testid="diger-kategori-yukleniyor" />;
  else if (q.isError) govde = <DetayMesaj testid="diger-kategori-hata">Kategoriler yüklenemedi.</DetayMesaj>;
  else if (satirlar.length === 0) govde = <DetayMesaj testid="diger-kategori-bos">Gösterilecek başka kategori yok.</DetayMesaj>;
  else
    govde = (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <AramaKutusu testid="diger-kategori-arama" placeholder="Kategori ara" value={arama} onChange={setArama} />
          <span data-testid="diger-kategori-sayac" className="text-[12px] text-ink-500 tnum">
            {gorunen.length} / {satirlar.length} kategori
          </span>
        </div>
        {gorunen.length === 0 ? (
          <DetayMesaj testid="diger-kategori-arama-bos">Aramaya uyan kategori yok.</DetayMesaj>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="diger-kategori-tablo" className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-[12px] text-ink-500 border-b border-border">
                  <th className="text-left font-medium py-2 pr-3">Kategori</th>
                  <th className="text-right font-medium py-2 px-3">Ciro</th>
                  <th className="text-right font-medium py-2 px-3">Pay</th>
                  <th className="text-right font-medium py-2 px-3">Marj</th>
                  <th className="text-right font-medium py-2 pl-3 whitespace-nowrap">A ürün</th>
                </tr>
              </thead>
              <tbody>
                {gorunen.map((k) => (
                  <tr key={k.ad} data-testid={`diger-kategori-satir-${k.ad}`} className="border-b border-border last:border-b-0">
                    <td className="py-2 pr-3 max-w-0 w-full">
                      <button
                        type="button"
                        data-testid={`diger-kategori-detay-${k.ad}`}
                        onClick={() => ac({ tur: 'kategori', kategori: k.ad })}
                        className="focus-ring block w-full truncate text-left text-ink-900 hover:underline underline-offset-2"
                      >
                        {k.ad}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-right tnum whitespace-nowrap text-ink-900">{fmtTRY(k.ciro)}</td>
                    <td className="py-2 px-3 text-right tnum text-ink-500">{oranMetni(k.pay, 1)}</td>
                    <td className={`py-2 px-3 text-right tnum ${k.marj !== null && k.marj < 0 ? 'text-neg-700' : 'text-ink-500'}`}>
                      {k.marj === null ? '—' : oranMetni(k.marj, 1)}
                    </td>
                    <td className="py-2 pl-3 text-right tnum text-ink-500">{fmtNum(k.aUrun)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );

  return (
    <div data-testid="diger-kategori-detay" className="flex flex-col gap-4">
      <DetayBolum baslik="Kategoriler">{govde}</DetayBolum>
    </div>
  );
}
